import {
  UpdateTermineFormProps,
  WeekdaySelectFormField,
} from '@/domain/termine/features/UpdateTermin';
import {
  TerminWiederholungsIntervall,
  TerminWochentage,
  WOCHENTAGE,
} from '@/domain/termine/shared/types';
import { getFixedWeekday } from '@/domain/termine/shared/utils/getWeekdaySelection';
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@e2n/ui';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function getSelectedWeekdayText(
  date: string,
  selectedWeekdays: TerminWochentage[] | undefined,
  translate: (key: string) => string,
) {
  const fixedWeekday = getFixedWeekday(dayjs(date));
  const selectedSet = new Set<TerminWochentage>([
    fixedWeekday,
    ...(selectedWeekdays ?? []),
  ]);

  return WOCHENTAGE.filter((weekday) => selectedSet.has(weekday.value))
    .map((weekday, index, arr) => {
      const isLast = index === arr.length - 1;

      return isLast
        ? `${translate(`general.date.days.${weekday.labelKey}Short`)}.`
        : `${translate(`general.date.days.${weekday.labelKey}Short`)}.,`;
    })
    .join(' ');
}

export function WeekdaySelectionDrawer({ form }: UpdateTermineFormProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <form.Subscribe
      selector={(state) => ({
        date: state.values.datum,
        selectedWeekdays: state.values.wochentagAuswahl,
        wiederholungsIntervall: state.values.wiederholungsIntervall,
      })}>
      {({ date, selectedWeekdays, wiederholungsIntervall }) => {
        if (wiederholungsIntervall !== TerminWiederholungsIntervall.WOCHE) {
          return null;
        }
        const selectedText = getSelectedWeekdayText(
          date,
          selectedWeekdays,
          (key) => t(key),
        );
        return (
          <Drawer open={open} onOpenChange={setOpen}>
            <div className="pb-3">
              <div className="text-xs text-start text-neutrals-900 font-bold pb-1">
                {t('domain.termine.sheet.fields.amRequired')}
              </div>
              <DrawerTrigger asChild>
                <Button
                  variant="outline-secondary"
                  className="w-full justify-between">
                  <span className="text-sm text-neutrals-900 font-normal">
                    {selectedText}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-neutrals-400"
                  />
                </Button>
              </DrawerTrigger>
            </div>
            <DrawerContent className="flex flex-col">
              <DrawerHeader className="shrink-0">
                <DrawerTitle className="text-sm text-center text-neutrals-900 font-bold">
                  {t('domain.termine.sheet.fields.am')}
                </DrawerTitle>
                <DrawerDescription className="sr-only">
                  {t('domain.termine.sheet.fields.am')}
                </DrawerDescription>
              </DrawerHeader>

              <div className="px-4 pb-4">
                <WeekdaySelectFormField form={form} />
              </div>

              <div className="p-4 pt-0 shrink-0">
                <DrawerClose asChild>
                  <Button className="w-full" size="lg">
                    {t('actions.next')}
                  </Button>
                </DrawerClose>
              </div>
            </DrawerContent>
          </Drawer>
        );
      }}
    </form.Subscribe>
  );
}
